import type { GridSkillTierLayout, ICoord2D } from '@nova/types';

import { tileIndexAt, tileIndexToColRow } from '../map-gen/tile-grid';
import { directionVectorToAngleDeg } from '../math-utils';

import { snapAngleToAnchorIndex } from './snap-to-anchor-index';
import type { CreateGridStampResolverArgs, GridStampResolver, GridStampResolveResult } from './types';

const DEFAULT_TARGETING_ANCHOR: ICoord2D = { x: 0, y: 0 };

function offsetKey(offset: ICoord2D): string {
  return `${offset.x},${offset.y}`;
}

function dedupeOffsets(offsets: readonly ICoord2D[]): readonly ICoord2D[] {
  const seen = new Set<string>();
  const deduped: ICoord2D[] = [];

  for (const offset of offsets) {
    const key = offsetKey(offset);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    deduped.push(offset);
  }

  return deduped;
}

function buildStampOffsetsByAnchor(layout: GridSkillTierLayout): readonly (readonly ICoord2D[])[] {
  const targetingAnchors = (layout.targetingAnchors && layout.targetingAnchors.length > 0)
    ? layout.targetingAnchors
    : [DEFAULT_TARGETING_ANCHOR];

  return targetingAnchors.map((anchor) => dedupeOffsets(
    layout.layout.map((cell) => ({
      x: anchor.x + cell.x,
      y: anchor.y + cell.y
    }))
  ));
}

function clampSnapIndex(snapIndex: number, anchorCount: number): number {
  return ((snapIndex % anchorCount) + anchorCount) % anchorCount;
}

/** Bake layout + match grid bounds into a resolver for per-frame snap and stamp placement. */
export function createGridStampResolver(args: CreateGridStampResolverArgs): GridStampResolver {
  const { layout, widthTiles, heightTiles } = args;
  const stampOffsetsByAnchor = buildStampOffsetsByAnchor(layout);
  const defaultStampOffsets = stampOffsetsByAnchor[0];
  const anchorCount = stampOffsetsByAnchor.length;

  function stampTileIndices(casterTileIndex: number, snapIndex: number): number[] {
    if (widthTiles <= 0 || heightTiles <= 0) { return []; }

    const maxIndexExclusive = widthTiles * heightTiles;
    if (casterTileIndex < 0 || casterTileIndex >= maxIndexExclusive) { return []; }

    const resolvedSnapIndex = clampSnapIndex(snapIndex, anchorCount);
    const offsets = stampOffsetsByAnchor[resolvedSnapIndex] ?? defaultStampOffsets;
    const { col: casterCol, row: casterRow } = tileIndexToColRow(casterTileIndex, widthTiles);
    const tileIndices: number[] = [];

    for (const offset of offsets) {
      const col = casterCol + offset.x;
      const row = casterRow + offset.y;

      if (col < 0 || row < 0 || col >= widthTiles || row >= heightTiles) { continue; }

      tileIndices.push(tileIndexAt(col, row, widthTiles));
    }

    return tileIndices;
  }

  return {
    anchorCount,
    widthTiles,
    heightTiles,

    snapIndexFromDirection(dir: ICoord2D): number {
      return snapAngleToAnchorIndex(directionVectorToAngleDeg(dir), anchorCount);
    },

    snapIndexFromAngleDeg(angleDeg: number): number {
      return snapAngleToAnchorIndex(angleDeg, anchorCount);
    },

    stampOffsetsForSnapIndex(snapIndex: number): readonly ICoord2D[] {
      return stampOffsetsByAnchor[clampSnapIndex(snapIndex, anchorCount)] ?? defaultStampOffsets;
    },

    stampTileIndices,

    resolveFromDirection(casterTileIndex: number, direction: ICoord2D): GridStampResolveResult {
      const snapIndex = snapAngleToAnchorIndex(directionVectorToAngleDeg(direction), anchorCount);

      return {
        snapIndex,
        tileIndices: stampTileIndices(casterTileIndex, snapIndex)
      };
    }
  };
}
